import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useVipBadges } from '../../hooks/useVipBadges';
import VipBadge from '../../components/base/VipBadge';
import { usePageSeo } from '../../utils/seo';

interface Store {
  id: number;
  name: string;
  logo: string;
  description: string;
  category: string;
  subcategory?: string;
  rating: number;
  isActive: boolean;
  createdAt: string;
  promos: any[];
}

export default function Stores() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Все');
  const [sortBy, setSortBy] = useState('popular');
  const [stores, setStores] = useState<Store[]>([]);
  const [categories, setCategories] = useState<string[]>(['Все']);
  const { getVipBadge, isHighlighted } = useVipBadges();

  usePageSeo({
    title: 'Все магазины с промокодами | Promokod.online',
    description: 'Каталог магазинов Promokod.online с актуальными промокодами, акциями и скидками. Найдите нужный бренд, выберите предложение и переходите к покупке.',
    canonicalPath: '/stores',
  });

  useEffect(() => {
    // Загружаем магазины из localStorage
    const savedStores = localStorage.getItem('admin_stores_v2') || localStorage.getItem('stores');
    if (savedStores) {
      const allStores = JSON.parse(savedStores);
      const activeStores = allStores.filter((store: Store) => store.isActive);
      setStores(activeStores);
      
      // Извлекаем уникальные категории
      const uniqueCategories = ['Все', ...new Set(activeStores.map((store: Store) => store.category))] as string[];
      setCategories(uniqueCategories);
    }
  }, []);

  // Обработка URL параметров для фильтрации по категории
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl && categories.includes(categoryFromUrl)) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams, categories]);

  const getStoreSlug = (storeName: string): string => {
    return storeName.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9а-яё\-]/gi, '')
      .replace(/\.ru$/, '')
      .replace(/\.com$/, '');
  };

  const handleViewPromocodes = (storeName: string) => {
    const slug = getStoreSlug(storeName);
    navigate(`/store/${slug}`);
  };

  const filteredStores = stores
    .filter(store => {
      const matchesSearch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           store.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'Все' || store.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // VIP-магазины всегда первыми
      const aVip = getVipBadge(String(a.id)) ? 1 : 0;
      const bVip = getVipBadge(String(b.id)) ? 1 : 0;
      if (bVip !== aVip) return bVip - aVip;

      switch (sortBy) {
        case 'popular':
          return (b.promos?.length || 0) - (a.promos?.length || 0);
        case 'rating':
          return b.rating - a.rating;
        case 'promocodes':
          return (b.promos?.length || 0) - (a.promos?.length || 0);
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {selectedCategory === 'Все' ? 'Все магазины с промокодами' : `Магазины: ${selectedCategory}`}
          </h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            {selectedCategory === 'Все' 
              ? `Более ${stores.length} популярных интернет-магазинов с актуальными промокодами и скидками`
              : `Лучшие магазины в категории "${selectedCategory}" с выгодными промокодами`
            }
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Поиск магазинов..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Категория:</span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Сортировка:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="popular">Популярные</option>
                <option value="rating">По рейтингу</option>
                <option value="promocodes">По количеству промокодов</option>
                <option value="name">По алфавиту</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Stores Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          {selectedCategory !== 'Все' && (
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-800">
                <i className="ri-filter-line"></i>
                <span className="font-medium">Показаны магазины категории: {selectedCategory}</span>
                <button
                  onClick={() => setSelectedCategory('Все')}
                  className="ml-auto text-blue-600 hover:text-blue-800 text-sm cursor-pointer"
                >
                  Сбросить фильтр
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStores.map(store => {
              const vip = getVipBadge(String(store.id));
              const highlighted = isHighlighted(String(store.id));
              return (
              <div key={store.id} className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border p-6 ${
                highlighted ? 'border-amber-300 ring-1 ring-amber-200 bg-amber-50/30' : 'border-gray-100'
              }`}>
                <div className="flex items-start gap-4">
                  <img
                    src={store.logo}
                    alt={store.name}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{store.name}</h3>
                      {vip && (
                        <VipBadge badge={vip.badge} badgeColor={vip.badgeColor} />
                      )}
                      {!vip && store.promos && store.promos.length > 10 && (
                        <span className="bg-orange-100 text-orange-600 text-xs px-2 py-1 rounded-full font-medium">
                          Популярный
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{store.description}</p>
                    
                    <div className="flex items-center justify-between text-sm">
                      {store.rating > 0 ? (
                        <div className="flex items-center gap-1 text-yellow-500">
                          <i className="ri-star-fill"></i>
                          <span className="text-gray-700 font-medium">{store.rating}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 font-medium">{store.category}</span>
                      )}
                      <span className="text-blue-600 font-medium">{store.promos?.length || 0} промокодов</span>
                    </div>
                    
                    <div className="mt-4">
                      <button 
                        onClick={() => handleViewPromocodes(store.name)}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        Смотреть промокоды
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {filteredStores.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-store-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Магазины не найдены</h3>
              <p className="text-gray-600">Попробуйте изменить параметры поиска</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
